import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { Sidebar } from "./Sidebar";
import { Header } from "./Header";
import { AcoesTopoProvider } from "./AcoesTopoContext";
import { AgenteIA } from "../components/agente/AgenteIA";
import { TourSistema } from "./TourSistema";
import { obterIdScrollPrincipal, rolarPrincipalParaTopo } from "./scrollToTop";
import { usePeriodoGlobalToken } from "../utils/periodoGlobal";
import { useJanelaHorarioWatcher } from "../auth/useJanelaHorarioWatcher";
import { useConfiguracoesSistemaPublicas } from "../utils/configuracoesSistema";
import { useRecursosApi } from "../utils/recursosApi";
import styles from "./LayoutPrincipal.module.css";
import { useIdioma } from "../i18n/IdiomaProvider";
import { AceiteTermosModal } from "./AceiteTermosModal";
import { AvatarPerfilModal } from "./AvatarPerfilModal";
import { useAuth } from "../auth/AuthContext";

function IconeAlternarMenu({ aberto }: { aberto: boolean }) {
  return (
    <svg
      className={`${styles.menuToggleSvg} ${aberto ? styles.menuToggleSvgAberto : ""}`}
      viewBox="0 0 24 24"
      width="16"
      height="16"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconeTour() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M12 10.2v5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <circle cx="12" cy="7.2" r="1" fill="currentColor" />
    </svg>
  );
}

function RodapeSistema({
  onReiniciarTour,
  tourAtivo,
  textoCopyright,
  textoVersao,
  exibirStatus,
}: {
  onReiniciarTour: () => void;
  tourAtivo: boolean;
  textoCopyright: string;
  textoVersao: string;
  exibirStatus: boolean;
}) {
  // O horário representa a abertura da tela, sem piscar nas atualizações de contexto.
  const [atualizacao] = useState(() => new Date().toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }));

  return (
    <footer className={styles.rodapeSistema}>
      <span>{textoCopyright}</span>
      <span className={styles.rodapeDireita}>
        {exibirStatus && (
          <>
            <span className={styles.rodapeAtualizacao}>Última Atualização: {atualizacao}</span>
            <span className={styles.rodapeVersao}>{textoVersao}</span>
          </>
        )}
        <button
          type="button"
          className={`${styles.botaoTour} tooltip tooltipUp`}
          data-tooltip={tourAtivo ? "Sobre" : "Tour de boas-vindo desativado"}
          aria-label="Reiniciar tour guiado"
          disabled={!tourAtivo}
          onClick={onReiniciarTour}
        >
          <IconeTour />
        </button>
      </span>
    </footer>
  );
}

export function LayoutPrincipal() {
  const location = useLocation();
  const { traduzir } = useIdioma();
  const { usuario, sair, recarregarSessao } = useAuth();
  const acessoRestrito = false;
  const periodoToken = usePeriodoGlobalToken();
  const configuracoes = useConfiguracoesSistemaPublicas();
  const recursos = useRecursosApi();
  useJanelaHorarioWatcher();
  const [recolhido, setRecolhido] = useState(false);
  const [buscaMenu, setBuscaMenu] = useState("");
  const [iaAberta, setIaAberta] = useState(false);
  const [iaModo, setIaModo] = useState<"PLATAFORMA" | "AREA_CLIENTE">("PLATAFORMA");
  const [restartTourToken, setRestartTourToken] = useState(0);
  const rotaFichaTecnica = location.pathname.startsWith("/vendas/ficha-tecnica");
  const menuAberto = !recolhido;
  const rotaDashboard = location.pathname === "/" || location.pathname.endsWith("/visao-geral");

  const menuToggleLeft = recolhido
    ? "calc(var(--sidebar-width-collapsed) - 12px)"
    : "calc(var(--sidebar-width) - 12px)";

  useEffect(() => {
    rolarPrincipalParaTopo("smooth");
  }, [location.pathname]);

  return (
    <AcoesTopoProvider
      iaVisivel={recursos.ia.visivel}
      iaAtiva={recursos.ia.habilitado}
      onAbrirIA={() => { setIaModo("PLATAFORMA"); setIaAberta(true); }}
    >
    <div className={styles.layout}>
      {!rotaFichaTecnica && (
        <Sidebar
          recolhido={recolhido}
          buscaMenu={buscaMenu}
          onAbrirMenu={() => setRecolhido(false)}
          onFecharMenu={() => setRecolhido(true)}
          onReiniciarTour={() => setRestartTourToken((atual) => atual + 1)}
          onAbrirChatCliente={() => { setIaModo("AREA_CLIENTE"); setIaAberta(true); }}
          chatClienteAberto={iaAberta && iaModo === "AREA_CLIENTE"}
        />
      )}

      {/* A Ficha Técnica usa só a marca no cabeçalho; não exibe sidebar nem expansor. */}
      {!rotaFichaTecnica && (
        <button
          type="button"
          data-tour="sidebar-toggle"
          className={styles.menuToggle}
          style={{ left: menuToggleLeft }}
          aria-label="Alternar menu"
          aria-expanded={menuAberto}
          disabled={acessoRestrito}
          onClick={() => setRecolhido((valor) => !valor)}
        >
          <IconeAlternarMenu aberto={menuAberto} />
        </button>
      )}

      <div className={styles.principal}>
        <Header
          nomeSistema={configuracoes.fantasia}
          buscaMenu={buscaMenu}
          onBuscaMenu={setBuscaMenu}
          onAbrirMenu={rotaFichaTecnica ? undefined : () => setRecolhido(false)}
          onAbrirIA={() => { setIaModo("PLATAFORMA"); setIaAberta(true); }}
          iaVisivel={recursos.ia.visivel}
          iaAtiva={recursos.ia.habilitado}
          tituloContextual={rotaFichaTecnica ? "Ficha técnica de fardamento" : undefined}
          subtituloContextual={rotaFichaTecnica ? "Monte o produto, os serviços, os preços e a arte antes de enviar para produção." : undefined}
          exibirMarcaRecolhida={rotaFichaTecnica}
        />
        <main
          id={obterIdScrollPrincipal()}
          className={`${styles.conteudo} ${rotaDashboard ? styles.conteudoDashboard : ""}`}
        >
          {/* Trocar login ou empresa desmonta a tela, seus dados, modais e paginação anteriores. */}
          <Outlet key={`${usuario?.codLogin}:${usuario?.selectedCodOrg ?? usuario?.codOrg}:${usuario?.codEmpresa}:${location.pathname}:${periodoToken}`} />
        </main>
        <RodapeSistema
          onReiniciarTour={() => setRestartTourToken((atual) => atual + 1)}
          tourAtivo={configuracoes.tourBoasVindas}
          textoCopyright={configuracoes.textoCopyright}
          textoVersao={configuracoes.textoVersao}
          exibirStatus
        />
      </div>

      <AgenteIA
        aberto={iaAberta && !acessoRestrito && (iaModo === "AREA_CLIENTE" || recursos.ia.visivel)}
        onFechar={() => setIaAberta(false)}
        modo={iaModo}
      />
      <TourSistema
        menuAberto={menuAberto}
        onAbrirMenu={() => setRecolhido(false)}
        onFecharMenu={() => setRecolhido(true)}
        restartToken={restartTourToken}
        tourAtivo={configuracoes.tourBoasVindas && !acessoRestrito}
      />
      <AceiteTermosModal />
      <AvatarPerfilModal
        aberto={!!usuario?.trocaSenhaObrigatoria || !!usuario?.configuracao2FAObrigatoria}
        onFechar={() => void sair().finally(() => window.location.assign("/login"))}
        fallbackNome={usuario?.nomePerfil || usuario?.email || "Usuário"}
        avatarSessao={usuario?.avatarId}
        forcarTrocaSenha={!!usuario?.trocaSenhaObrigatoria}
        forcar2FA={!!usuario?.configuracao2FAObrigatoria && !usuario?.trocaSenhaObrigatoria}
        onAtualizado={recarregarSessao}
      />
    </div>
    </AcoesTopoProvider>
  );
}
